import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Visit, Workplace } from '../../types';
import { visitApi, workplaceApi } from '../../services/api';
import { formatDate, canMarkVisitAsCompleted } from '../../utils/dateUtils';
import { exportMonthlyPlanningToExcel } from '../../utils/excelExport';
import PlanVisitModal from '../../components/Workplace/PlanVisitModal';
import WorkplaceDetailModal from '../../components/Workplace/WorkplaceDetailModal';
import Swal from 'sweetalert2';
import './PlanningPage.css';

type ViewMode = 'daily' | 'weekly' | 'monthly' | 'list';

export const PlanningPage: React.FC = () => {
  const [visits, setVisits] = useState<Visit[]>([]);
  const [workplaces, setWorkplaces] = useState<Workplace[]>([]);
  const [loading, setLoading] = useState(true);

  // Görünüm Modu: daily, weekly, monthly, list
  const [viewMode, setViewMode] = useState<ViewMode>('weekly');

  // Filtreler
  const [selectedWorkplaceId, setSelectedWorkplaceId] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'planned' | 'completed'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Aktif Tarih Seçimi (YYYY-MM-DD)
  const todayStr = useMemo(() => new Date().toISOString().split('T')[0], []);
  const [currentDate, setCurrentDate] = useState<string>(todayStr);

  // Modal State'leri
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [modalInitialDate, setModalInitialDate] = useState<string | undefined>(undefined);
  const [selectedWpForModal, setSelectedWpForModal] = useState<Workplace | null>(null);

  // İşyeri Detay Modalı State
  const [detailWorkplace, setDetailWorkplace] = useState<Workplace | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Verileri Backend'den Çek
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [visitsRes, wpRes] = await Promise.all([
        visitApi.getAll(),
        workplaceApi.getAll()
      ]);

      if (visitsRes.success && visitsRes.data) {
        setVisits(visitsRes.data);
      }
      if (wpRes.success && wpRes.data) {
        setWorkplaces(wpRes.data);
      }
    } catch (err) {
      console.error('Planlama verileri yüklenirken hata:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filtrelenmiş Ziyaretler Listesi
  const filteredVisits = useMemo(() => {
    return visits.filter((v) => {
      // İşyeri filtresi
      if (selectedWorkplaceId !== 'all' && v.workplaceId !== Number(selectedWorkplaceId)) {
        return false;
      }
      // Durum filtresi
      if (statusFilter !== 'all' && v.status !== statusFilter) {
        return false;
      }
      // Arama filtresi
      if (searchTerm.trim()) {
        const q = searchTerm.toLocaleLowerCase('tr-TR');
        const wpTitle = (v.workplace?.title || '').toLocaleLowerCase('tr-TR');
        const notes = (v.notes || '').toLocaleLowerCase('tr-TR');
        const type = (v.visitType || '').toLocaleLowerCase('tr-TR');
        if (!wpTitle.includes(q) && !notes.includes(q) && !type.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [visits, selectedWorkplaceId, statusFilter, searchTerm]);

  // İstatistikler (KPI)
  const stats = useMemo(() => {
    const plannedCount = visits.filter(v => v.status === 'planned').length;
    const completedCount = visits.filter(v => v.status === 'completed').length;

    // Bu haftaki planlananlar
    const now = new Date();
    const dayOfWeek = now.getDay() === 0 ? 7 : now.getDay();
    const mon = new Date(now);
    mon.setDate(now.getDate() - (dayOfWeek - 1));
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const monStr = mon.toISOString().split('T')[0];
    const sunStr = sun.toISOString().split('T')[0];

    const thisWeekPlanned = visits.filter(v => v.status === 'planned' && v.visitDate >= monStr && v.visitDate <= sunStr).length;

    // Bu ayki planlananlar
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    const thisMonthPlanned = visits.filter(v => v.status === 'planned' && v.visitDate.startsWith(monthPrefix)).length;

    return {
      totalPlanned: plannedCount,
      thisWeekPlanned,
      thisMonthPlanned,
      totalCompleted: completedCount
    };
  }, [visits]);

  // Ziyareti Tamamlandı Olarak İşaretle
  const handleCompleteVisit = async (visitId: number) => {
    const visit = visits.find(v => v.id === visitId);
    if (!visit) return;

    // KURAL: Ziyaret günü ve saati gelmeden 'YAPILDI' olarak işaretlenemez!
    const timeCheck = canMarkVisitAsCompleted(visit.visitDate, visit.startTime);
    if (!timeCheck.allowed) {
      Swal.fire({
        icon: 'warning',
        title: 'Ziyaret Henüz Gerçekleşemez',
        text: timeCheck.reason || 'Ziyaret günü ve saati gelmeden "YAPILDI" olarak işaretlenemez.',
        confirmButtonColor: '#e11d48'
      });
      return;
    }

    const { value: notes } = await Swal.fire({
      title: 'Ziyareti Tamamla',
      text: `"${visit.workplace?.title || 'Firma'}" ziyaretini tamamlandı olarak işaretlemek istiyor musunuz?`,
      input: 'textarea',
      inputLabel: 'Saha Denetim Notu (İsteğe Bağlı)',
      inputValue: visit.notes || '',
      inputPlaceholder: 'Saha denetiminde tespit edilen hususlar, öneriler...',
      showCancelButton: true,
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#64748b',
      confirmButtonText: '✓ Ziyareti Tamamla',
      cancelButtonText: 'Vazgeç'
    });

    if (notes !== undefined) {
      try {
        const res = await visitApi.updateStatus(visitId, {
          status: 'completed',
          notes: notes ? notes.trim() : visit.notes
        });
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Tamamlandı!',
            text: 'Saha ziyareti başarıyla tamamlandı olarak kaydedildi.',
            timer: 1600,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err.response?.data?.message || 'Ziyaret güncellenirken hata oluştu.'
        });
      }
    }
  };

  // Ziyareti Sil
  const handleDeleteVisit = async (visitId: number) => {
    const visit = visits.find(v => v.id === visitId);
    const confirmed = await Swal.fire({
      title: 'Ziyareti Sil?',
      text: `"${visit?.workplace?.title || 'Bu'}" için planlanan ziyaret kaydını silmek istediğinize emin misiniz?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Evet, Sil',
      cancelButtonText: 'Vazgeç'
    });

    if (confirmed.isConfirmed) {
      try {
        const res = await visitApi.delete(visitId);
        if (res.success) {
          Swal.fire({
            icon: 'success',
            title: 'Silindi',
            text: 'Ziyaret kaydı başarıyla silindi.',
            timer: 1400,
            showConfirmButton: false
          });
          loadData();
        }
      } catch (err: any) {
        Swal.fire({
          icon: 'error',
          title: 'Hata',
          text: err.response?.data?.message || 'Ziyaret silinirken hata oluştu.'
        });
      }
    }
  };

  // Yeni Ziyaret Modalı Aç
  const handleOpenPlanModal = (targetDate?: string, targetWp?: Workplace) => {
    setModalInitialDate(targetDate || currentDate || todayStr);
    setSelectedWpForModal(targetWp || null);
    setIsPlanModalOpen(true);
  };

  // İşyeri Detay Kartını Aç
  const handleOpenWorkplaceDetail = (wpId: number) => {
    const wp = workplaces.find(w => w.id === wpId);
    if (wp) {
      setDetailWorkplace(wp);
      setIsDetailModalOpen(true);
    }
  };

  // =========================================================================
  // TARİH HESAPLAMA YARDIMCILARI
  // =========================================================================
  const curr = useMemo(() => new Date(currentDate + 'T00:00:00'), [currentDate]);

  // Günlük Değişim
  const handleDayOffset = (offset: number) => {
    const d = new Date(curr);
    d.setDate(d.getDate() + offset);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  // Haftalık Aralık (Pazartesi - Pazar)
  const weekDays = useMemo(() => {
    const dayOfWeek = curr.getDay() === 0 ? 7 : curr.getDay();
    const mon = new Date(curr);
    mon.setDate(curr.getDate() - (dayOfWeek - 1));

    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      const iso = d.toISOString().split('T')[0];
      const dayNames = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
      const shortDayNames = ['Pzr', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];
      days.push({
        dateStr: iso,
        dayNum: d.getDate(),
        monthNum: d.getMonth() + 1,
        dayName: dayNames[d.getDay()],
        shortDayName: shortDayNames[d.getDay()],
        isToday: iso === todayStr
      });
    }
    return days;
  }, [curr, todayStr]);

  const handleWeekOffset = (offset: number) => {
    const d = new Date(curr);
    d.setDate(d.getDate() + offset * 7);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  // Aylık Matris
  const monthInfo = useMemo(() => {
    const year = curr.getFullYear();
    const month = curr.getMonth(); // 0-11
    const monthNames = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];

    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    // Ayın ilk gününün haftanın hangi günü olduğu (1: Pzt ... 7: Pzr)
    const firstDayWeek = firstDayOfMonth.getDay() === 0 ? 7 : firstDayOfMonth.getDay();
    const totalDays = lastDayOfMonth.getDate();

    // Takvim hücreleri (Önceki ayın kalan günleri + bu ay + sonraki ayın günleri)
    const matrixDays = [];

    // Önceki ayın dolgu günleri
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = firstDayWeek - 1; i > 0; i--) {
      const d = prevMonthLastDay - i + 1;
      const prevDate = new Date(year, month - 1, d);
      matrixDays.push({
        dateStr: prevDate.toISOString().split('T')[0],
        dayNum: d,
        isCurrentMonth: false,
        isToday: prevDate.toISOString().split('T')[0] === todayStr
      });
    }

    // Bu ayın günleri
    for (let d = 1; d <= totalDays; d++) {
      const curDate = new Date(year, month, d);
      const iso = curDate.toISOString().split('T')[0];
      matrixDays.push({
        dateStr: iso,
        dayNum: d,
        isCurrentMonth: true,
        isToday: iso === todayStr
      });
    }

    // Sonraki ayın dolgu günleri (42 hücreye veya 35 hücreye tamamla)
    const remaining = 35 - matrixDays.length >= 0 ? 35 - matrixDays.length : 42 - matrixDays.length;
    for (let d = 1; d <= remaining; d++) {
      const nextDate = new Date(year, month + 1, d);
      matrixDays.push({
        dateStr: nextDate.toISOString().split('T')[0],
        dayNum: d,
        isCurrentMonth: false,
        isToday: nextDate.toISOString().split('T')[0] === todayStr
      });
    }

    return {
      year,
      monthName: monthNames[month],
      label: `${monthNames[month]} ${year}`,
      matrixDays
    };
  }, [curr, todayStr]);

  const handleMonthOffset = (offset: number) => {
    const d = new Date(curr);
    d.setMonth(d.getMonth() + offset);
    setCurrentDate(d.toISOString().split('T')[0]);
  };

  // Tehlike Sınıfı Rozeti
  const renderDangerBadge = (dangerClass?: string) => {
    const raw = (dangerClass || '').toLowerCase().trim();
    if (raw.includes('çok') || raw.includes('cok') || raw.includes('very')) {
      return <span className="plan-danger-pill danger-very">Çok Tehlikeli</span>;
    }
    if (raw.includes('tehlike') || raw.includes('dangerous')) {
      return <span className="plan-danger-pill danger-med">Tehlikeli</span>;
    }
    return <span className="plan-danger-pill danger-low">Az Tehlikeli</span>;
  };

  // Geçmiş / Gelecek veya Seçili Ayın Ziyaret Planını Excel Olarak Dışa Aktar (Tarih Seçimli)
  const handleExportMonthlyExcel = async () => {
    const monthNamesList = [
      'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
      'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
    ];

    // Aktif sayfa tarihi varsayılan olsun
    const [currentY, currentM] = currentDate.split('-');
    const defaultYear = parseInt(currentY, 10);
    const defaultMonth = parseInt(currentM, 10);

    const yearOptions = [
      defaultYear - 2,
      defaultYear - 1,
      defaultYear,
      defaultYear + 1,
      defaultYear + 2
    ];

    const htmlContent = `
      <div style="text-align: left; font-size: 13px; color: #1e293b; padding: 6px 0;">
        <p style="margin-bottom: 12px; color: #64748b; font-size: 12px;">
          Raporunu almak istediğiniz <strong>Yıl ve Ayı</strong> seçiniz:
        </p>
        <div style="display: flex; gap: 10px; margin-bottom: 12px;">
          <div style="flex: 1;">
            <label style="font-weight: 700; display: block; margin-bottom: 4px; font-size: 11.5px;">Ay:</label>
            <select id="swal-export-month" class="swal2-input" style="margin: 0; width: 100%; height: 38px; font-size: 13px; font-weight: 700;">
              ${monthNamesList.map((mName, idx) => `
                <option value="${idx + 1}" ${idx + 1 === defaultMonth ? 'selected' : ''}>
                  ${mName}
                </option>
              `).join('')}
            </select>
          </div>
          <div style="width: 110px;">
            <label style="font-weight: 700; display: block; margin-bottom: 4px; font-size: 11.5px;">Yıl:</label>
            <select id="swal-export-year" class="swal2-input" style="margin: 0; width: 100%; height: 38px; font-size: 13px; font-weight: 700;">
              ${yearOptions.map(y => `
                <option value="${y}" ${y === defaultYear ? 'selected' : ''}>${y}</option>
              `).join('')}
            </select>
          </div>
        </div>
        <p style="font-size: 11px; color: #0369a1; background: #e0f2fe; padding: 6px 10px; border-radius: 6px; margin: 0; border: 1px solid #bae6fd;">
          💡 İpucu: Seçtiğiniz ayda filtreye uyan tüm planlanmış ve tamamlanmış ziyaretler Excel sayfasına aktarılacaktır.
        </p>
      </div>
    `;

    const { value: formValues } = await Swal.fire({
      title: '📊 Aylık Plan Excel İndir',
      html: htmlContent,
      showCancelButton: true,
      confirmButtonText: '📥 Excel İndir',
      cancelButtonText: 'Vazgeç',
      confirmButtonColor: '#059669',
      cancelButtonColor: '#64748b',
      focusConfirm: false,
      preConfirm: () => {
        const monthEl = document.getElementById('swal-export-month') as HTMLSelectElement;
        const yearEl = document.getElementById('swal-export-year') as HTMLSelectElement;
        return {
          month: parseInt(monthEl.value, 10),
          year: parseInt(yearEl.value, 10)
        };
      }
    });

    if (!formValues) return;

    const { year, month } = formValues;
    const monthPad = String(month).padStart(2, '0');
    const targetMonthPrefix = `${year}-${monthPad}`;
    const selectedMonthLabel = `${monthNamesList[month - 1]} ${year}`;

    // Seçilen aya ait ziyaretleri filtrele ve workplace verisini garantiye al
    const monthVisits = filteredVisits
      .filter(v => (v.visitDate || '').startsWith(targetMonthPrefix))
      .map(v => {
        const fullWp = v.workplace || workplaces.find(w => w.id === v.workplaceId);
        return {
          ...v,
          workplace: fullWp || v.workplace
        };
      });

    if (monthVisits.length === 0) {
      Swal.fire({
        title: 'Kayıt Bulunamadı',
        text: `${selectedMonthLabel} dönemi için dışa aktarılacak ziyaret kaydı bulunmuyor.`,
        icon: 'info',
        confirmButtonColor: '#1e3c72'
      });
      return;
    }

    try {
      exportMonthlyPlanningToExcel(selectedMonthLabel, monthVisits);
      Swal.fire({
        title: 'Excel İndirildi',
        text: `${selectedMonthLabel} ziyaret programı (${monthVisits.length} kayıt) başarıyla Excel tablosuna aktarıldı.`,
        icon: 'success',
        timer: 2200,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Excel aktarım hatası:', err);
      Swal.fire({
        title: 'Hata',
        text: 'Excel dosyası oluşturulurken bir hata oluştu.',
        icon: 'error',
        confirmButtonColor: '#1e3c72'
      });
    }
  };

  return (
    <div className="planning-page-container">
      {/* Üst Kompakt Başlık, KPI ve Aksiyon Alanı */}
      <div className="planning-top-bar">
        <div className="planning-top-left">
          <div className="planning-brand-chip">
            <span className="brand-icon">📅</span>
            <span className="brand-title">Planlama</span>
          </div>

          {/* Kompakt KPI Sayıcıları */}
          <div className="planning-kpi-chips">
            <div className="kpi-chip amber" title="Bu Hafta Planlanan Ziyaretler">
              <span className="kpi-dot"></span>
              <span className="kpi-lbl">Bu Hafta:</span>
              <strong>{stats.thisWeekPlanned}</strong>
            </div>
            <div className="kpi-chip blue" title="Bu Ay Planlanan Ziyaretler">
              <span className="kpi-dot"></span>
              <span className="kpi-lbl">Bu Ay:</span>
              <strong>{stats.thisMonthPlanned}</strong>
            </div>
            <div className="kpi-chip purple" title="Toplam Bekleyen Planlı Ziyaretler">
              <span className="kpi-dot"></span>
              <span className="kpi-lbl">Bekleyen:</span>
              <strong>{stats.totalPlanned}</strong>
            </div>
            <div className="kpi-chip green" title="Tamamlanan / Gerçekleşen Ziyaretler">
              <span className="kpi-dot"></span>
              <span className="kpi-lbl">Yapıldı:</span>
              <strong>{stats.totalCompleted}</strong>
            </div>
          </div>
        </div>

        <div className="planning-top-right">
          <button
            type="button"
            className="btn-export-excel-plan"
            onClick={handleExportMonthlyExcel}
            title="Geçmiş, Gelecek veya Seçili Ayın Ziyaret Planını Excel Olarak İndir"
          >
            📊 Aylık Plan Excel İndir
          </button>
          <button
            type="button"
            className="btn-add-plan-primary"
            onClick={() => handleOpenPlanModal()}
          >
            ➕ Yeni Ziyaret
          </button>
        </div>
      </div>

      {/* Kontrol ve Navigasyon Birleşik Barı */}
      <div className="planning-controls-bar">
        {/* Görünüm Seçici Sekmeler (Günlük / Haftalık / Aylık / Liste) */}
        <div className="view-mode-tabs">
          <button
            type="button"
            className={`view-tab ${viewMode === 'daily' ? 'active' : ''}`}
            onClick={() => setViewMode('daily')}
          >
            📆 Günlük
          </button>
          <button
            type="button"
            className={`view-tab ${viewMode === 'weekly' ? 'active' : ''}`}
            onClick={() => setViewMode('weekly')}
          >
            🗓️ Haftalık
          </button>
          <button
            type="button"
            className={`view-tab ${viewMode === 'monthly' ? 'active' : ''}`}
            onClick={() => setViewMode('monthly')}
          >
            📊 Aylık
          </button>
          <button
            type="button"
            className={`view-tab ${viewMode === 'list' ? 'active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            📋 Liste
          </button>
        </div>

        {/* Filtreler: İşyeri, Durum ve Arama */}
        <div className="planning-filters-wrap">
          {/* İşyeri Filtresi */}
          <select
            className="plan-filter-select"
            value={selectedWorkplaceId}
            onChange={(e) => setSelectedWorkplaceId(e.target.value)}
          >
            <option value="all">🏢 Tüm Firmalar ({workplaces.length})</option>
            {workplaces.map((wp) => (
              <option key={wp.id} value={wp.id}>
                {wp.title}
              </option>
            ))}
          </select>

          {/* Durum Filtresi */}
          <select
            className="plan-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
          >
            <option value="all">Tüm Durumlar</option>
            <option value="planned">📅 Sadece Planlananlar</option>
            <option value="completed">✅ Sadece Tamamlananlar</option>
          </select>

          {/* Arama Kutusu */}
          <div className="plan-search-box">
            <span>🔍</span>
            <input
              type="text"
              placeholder="Firma veya not ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {searchTerm && (
              <button
                type="button"
                className="plan-search-clear"
                onClick={() => setSearchTerm('')}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Navigasyon Barı (Tarih Değiştirme) */}
      <div className="planning-calendar-nav">
        <div className="nav-buttons-left">
          {viewMode === 'daily' && (
            <>
              <button type="button" className="btn-nav-step" onClick={() => handleDayOffset(-1)}>
                ◀ Önceki Gün
              </button>
              <button type="button" className="btn-nav-today" onClick={() => setCurrentDate(todayStr)}>
                Bugün
              </button>
              <button type="button" className="btn-nav-step" onClick={() => handleDayOffset(1)}>
                Sonraki Gün ▶
              </button>
            </>
          )}

          {viewMode === 'weekly' && (
            <>
              <button type="button" className="btn-nav-step" onClick={() => handleWeekOffset(-1)}>
                ◀ Önceki Hafta
              </button>
              <button type="button" className="btn-nav-today" onClick={() => setCurrentDate(todayStr)}>
                Bu Hafta
              </button>
              <button type="button" className="btn-nav-step" onClick={() => handleWeekOffset(1)}>
                Sonraki Hafta ▶
              </button>
            </>
          )}

          {viewMode === 'monthly' && (
            <>
              <button type="button" className="btn-nav-step" onClick={() => handleMonthOffset(-1)}>
                ◀ Önceki Ay
              </button>
              <button type="button" className="btn-nav-today" onClick={() => setCurrentDate(todayStr)}>
                Bu Ay
              </button>
              <button type="button" className="btn-nav-step" onClick={() => handleMonthOffset(1)}>
                Sonraki Ay ▶
              </button>
            </>
          )}

          {viewMode === 'list' && (
            <span style={{ fontSize: '13px', fontWeight: 700, color: '#475569' }}>
              Toplam {filteredVisits.length} Ziyaret Kaydı Listeleniyor
            </span>
          )}
        </div>

        {/* Orta Başlık */}
        <div className="nav-title-center">
          {viewMode === 'daily' && (
            <h2>
              {formatDate(currentDate)}{' '}
              <span className="sub-day-name">({weekDays.find(w => w.dateStr === currentDate)?.dayName || ''})</span>
            </h2>
          )}
          {viewMode === 'weekly' && (
            <h2>
              {formatDate(weekDays[0].dateStr)} &mdash; {formatDate(weekDays[6].dateStr)} Haftalık Programı
            </h2>
          )}
          {viewMode === 'monthly' && (
            <h2>{monthInfo.label}</h2>
          )}
        </div>

        {/* Hızlı Tarih Seçici */}
        <div className="nav-picker-right">
          <input
            type="date"
            className="plan-date-picker-input"
            value={currentDate}
            onChange={(e) => e.target.value && setCurrentDate(e.target.value)}
            title="Belirli Bir Tarihe Git"
          />
        </div>
      </div>

      {/* Yükleniyor Göstergesi */}
      {loading ? (
        <div className="planning-loading-state">
          <div className="loading-spinner"></div>
          <p>Ziyaret programı yükleniyor...</p>
        </div>
      ) : (
        /* GÖRÜNÜM İÇERİKLERİ */
        <div className="planning-main-view">
          {/* ========================================================= */}
          {/* 1. GÜNLÜK GÖRÜNÜM (DAILY VIEW)                             */}
          {/* ========================================================= */}
          {viewMode === 'daily' && (
            <div className="daily-view-container">
              {(() => {
                const dayVisits = filteredVisits.filter(v => v.visitDate === currentDate);
                if (dayVisits.length === 0) {
                  return (
                    <div className="empty-day-state">
                      <span className="empty-emoji">📅</span>
                      <h3>Bu Tarihte Planlanmış Ziyaret Yok</h3>
                      <p>
                        <strong>{formatDate(currentDate)}</strong> günü için henüz bir ziyaret planı bulunmuyor.
                      </p>
                      <button
                        type="button"
                        className="btn-add-plan-secondary"
                        onClick={() => handleOpenPlanModal(currentDate)}
                      >
                        ➕ Bu Güne Ziyaret Planla
                      </button>
                    </div>
                  );
                }

                return (
                  <div className="daily-timeline-list">
                    {dayVisits.map((v) => {
                      const isPlanned = v.status === 'planned';
                      return (
                        <div key={v.id} className={`daily-visit-card ${isPlanned ? 'status-planned' : 'status-completed'}`}>
                          <div className="card-time-column">
                            <span className="time-badge">
                              {v.startTime || '09:00'} &mdash; {v.endTime || '11:00'}
                            </span>
                            <span className={`status-badge-pill ${isPlanned ? 'planned' : 'completed'}`}>
                              {isPlanned ? '📅 Planlandı' : '✅ Tamamlandı'}
                            </span>
                          </div>

                          <div className="card-content-column">
                            <div className="card-title-row">
                              <h3
                                className="workplace-link"
                                onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                                title="İşyeri Kartını Aç"
                              >
                                🏢 {v.workplace?.title || 'Bilinmeyen Firma'}
                              </h3>
                              {renderDangerBadge(v.workplace?.dangerClass)}
                            </div>

                            <div className="card-meta-line">
                              <span>📋 <strong>Tür:</strong> {v.visitType}</span>
                              {v.workplace?.city && (
                                <span>📍 {v.workplace.city} {v.workplace.district ? `/${v.workplace.district}` : ''}</span>
                              )}
                              {v.workplace?.contactPerson && (
                                <span>👤 {v.workplace.contactPerson} ({v.workplace.contactPhone || 'Tel yok'})</span>
                              )}
                            </div>

                            {v.notes && (
                              <div className="card-notes-box">
                                <strong>Saha Notu:</strong> {v.notes}
                              </div>
                            )}
                          </div>

                          <div className="card-actions-column">
                            {isPlanned && (() => {
                              const check = canMarkVisitAsCompleted(v.visitDate, v.startTime);
                              const isEligible = check.allowed;
                              return (
                                <button
                                  type="button"
                                  className={`btn-card-action green ${!isEligible ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  onClick={() => handleCompleteVisit(v.id)}
                                  title={isEligible ? 'Ziyareti Tamamlandı Olarak İşaretle' : (check.reason || 'Ziyaret günü ve saati gelmeden tamamlanamaz')}
                                  style={!isEligible ? { opacity: 0.5, cursor: 'not-allowed', filter: 'grayscale(0.6)' } : undefined}
                                >
                                  ✓ Tamamla
                                </button>
                              );
                            })()}
                            <button
                              type="button"
                              className="btn-card-action blue"
                              onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                              title="İşyeri Kartını Aç"
                            >
                              Detay
                            </button>
                            <button
                              type="button"
                              className="btn-card-action red"
                              onClick={() => handleDeleteVisit(v.id)}
                              title="Ziyareti Sil"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ========================================================= */}
          {/* 2. HAFTALIK GÖRÜNÜM (WEEKLY VIEW)                            */}
          {/* ========================================================= */}
          {viewMode === 'weekly' && (
            <div className="weekly-grid-container">
              {weekDays.map((day) => {
                const dayVisits = filteredVisits.filter(v => v.visitDate === day.dateStr);

                return (
                  <div key={day.dateStr} className={`weekly-day-col ${day.isToday ? 'today' : ''}`}>
                    {/* Gün Başlığı */}
                    <div className="day-col-header">
                      <div className="day-col-title">
                        <span className="day-name">{day.shortDayName}</span>
                        <span className="day-date">{day.dayNum}</span>
                      </div>
                      <button
                        type="button"
                        className="btn-quick-add-mini"
                        onClick={() => handleOpenPlanModal(day.dateStr)}
                        title={`${formatDate(day.dateStr)} Gününe Ziyaret Planla`}
                      >
                        +
                      </button>
                    </div>

                    {/* Günün Ziyaretleri */}
                    <div className="day-col-body">
                      {dayVisits.length === 0 ? (
                        <div className="no-visit-placeholder">Ziyaret yok</div>
                      ) : (
                        dayVisits.map((v) => {
                          const isPlanned = v.status === 'planned';
                          return (
                            <div
                              key={v.id}
                              className={`weekly-visit-mini-card ${isPlanned ? 'planned' : 'completed'}`}
                              onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                              title={`${v.workplace?.title}\nSaat: ${v.startTime}-${v.endTime}\nTür: ${v.visitType}\nDurum: ${isPlanned ? 'Planlandı' : 'Tamamlandı'}`}
                            >
                              <div className="mini-card-time">
                                ⏰ {v.startTime || '09:00'} - {v.endTime || '11:00'}
                              </div>
                              <div className="mini-card-title">
                                {v.workplace?.title || 'Firma'}
                              </div>
                              <div className="mini-card-footer">
                                <span className={`mini-status ${isPlanned ? 'amber' : 'green'}`}>
                                  {isPlanned ? 'Planlı' : '✓ Yapıldı'}
                                </span>
                                <button
                                  type="button"
                                  className="mini-del-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteVisit(v.id);
                                  }}
                                  title="Sil"
                                >
                                  ×
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* ========================================================= */}
          {/* 3. AYLIK GÖRÜNÜM (MONTHLY VIEW — Yan Yana Tek Ekran)         */}
          {/* ========================================================= */}
          {viewMode === 'monthly' && (
            <div className="monthly-view-layout">
              <div className="monthly-grid-side">
                <div className="monthly-calendar-grid">
                  {/* Hafta Günleri Başlıkları */}
                  {['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Pzr'].map((name) => (
                    <div key={name} className="monthly-week-header-cell">
                      {name}
                    </div>
                  ))}

                  {/* Gün Hücreleri */}
                  {monthInfo.matrixDays.map((cell) => {
                    const dayVisits = filteredVisits.filter(v => v.visitDate === cell.dateStr);
                    const isSelected = cell.dateStr === currentDate;

                    return (
                      <div
                        key={cell.dateStr}
                        className={`monthly-day-cell ${cell.isCurrentMonth ? '' : 'outside'} ${cell.isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                        onClick={() => setCurrentDate(cell.dateStr)}
                      >
                        <div className="cell-top">
                          <span className="cell-num">{cell.dayNum}</span>
                          {dayVisits.length > 0 && (
                            <span className="cell-count-badge">{dayVisits.length}</span>
                          )}
                        </div>

                        <div className="cell-visits-list">
                          {dayVisits.slice(0, 2).map((v) => (
                            <div
                              key={v.id}
                              className={`cell-visit-tag ${v.status === 'planned' ? 'planned' : 'completed'}`}
                              title={`${v.workplace?.title} (${v.startTime}-${v.endTime})`}
                            >
                              <span className="dot"></span>
                              <span className="tag-text">{v.startTime} {v.workplace?.title}</span>
                            </div>
                          ))}
                          {dayVisits.length > 2 && (
                            <div className="cell-more-tag">+{dayVisits.length - 2} daha</div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Sağ Yan Panel: Seçili Gün Detay Çekmecesi */}
              <div className="monthly-side-panel">
                <div className="selected-day-header">
                  <h3>📌 {formatDate(currentDate)}</h3>
                  <button
                    type="button"
                    className="btn-add-plan-secondary"
                    onClick={() => handleOpenPlanModal(currentDate)}
                    title="Bu Güne Yeni Ziyaret Ekle"
                  >
                    ➕ Ekle
                  </button>
                </div>

                {(() => {
                  const dayVisits = filteredVisits.filter(v => v.visitDate === currentDate);
                  if (dayVisits.length === 0) {
                    return (
                      <div className="no-visits-selected-day">
                        Bu günde planlanmış ziyaret bulunmuyor.
                      </div>
                    );
                  }

                  return (
                    <div className="selected-day-cards-list">
                      {dayVisits.map((v) => (
                        <div key={v.id} className="selected-day-mini-row">
                          <div className="time-col">
                            <strong>{v.startTime || '09:00'} - {v.endTime || '11:00'}</strong>
                          </div>
                          <div className="wp-col">
                            <span
                              className="wp-title-link"
                              onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                            >
                              {v.workplace?.title}
                            </span>
                            <span className="wp-type-sub">{v.visitType}</span>
                          </div>
                          <div className="status-col">
                            <span className={`status-pill ${v.status}`}>
                              {v.status === 'planned' ? '📅 Planlandı' : '✅ Tamamlandı'}
                            </span>
                          </div>
                          <div className="action-col">
                            {v.status === 'planned' && (() => {
                              const check = canMarkVisitAsCompleted(v.visitDate, v.startTime);
                              const isEligible = check.allowed;
                              return (
                                <button
                                  type="button"
                                  className={`btn-mini-act green ${!isEligible ? 'opacity-50 cursor-not-allowed' : ''}`}
                                  onClick={() => handleCompleteVisit(v.id)}
                                  title={isEligible ? 'Ziyareti Tamamla' : (check.reason || 'Ziyaret günü ve saati gelmeden tamamlanamaz')}
                                  style={!isEligible ? { opacity: 0.5, cursor: 'not-allowed', filter: 'grayscale(0.6)' } : undefined}
                                >
                                  ✓
                                </button>
                              );
                            })()}
                            <button
                              type="button"
                              className="btn-mini-act red"
                              onClick={() => handleDeleteVisit(v.id)}
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* ========================================================= */}
          {/* 4. LİSTE / AJANDA GÖRÜNÜMÜ (AGENDA VIEW)                    */}
          {/* ========================================================= */}
          {viewMode === 'list' && (
            <div className="list-view-container">
              {filteredVisits.length === 0 ? (
                <div className="empty-day-state">
                  <span className="empty-emoji">📋</span>
                  <h3>Kayıtlı Ziyaret Bulunamadı</h3>
                  <p>Arama veya filtre kriterlerinize uyan ziyaret kaydı bulunmuyor.</p>
                </div>
              ) : (
                <table className="planning-table">
                  <thead>
                    <tr>
                      <th style={{ width: '105px' }}>Plan Tarihi</th>
                      <th style={{ width: '105px' }}>Saat Aralığı</th>
                      <th>İşyeri Unvanı</th>
                      <th style={{ width: '110px' }}>Tehlike Sınıfı</th>
                      <th>Ziyaret Türü</th>
                      <th>Saha Notu</th>
                      <th style={{ width: '110px', textAlign: 'center' }}>Durum</th>
                      <th style={{ width: '120px', textAlign: 'center' }}>İşlemler</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVisits.map((v) => {
                      const isPlanned = v.status === 'planned';
                      return (
                        <tr key={v.id} className={isPlanned ? 'row-planned' : 'row-completed'}>
                          <td style={{ fontWeight: 700 }}>{formatDate(v.visitDate)}</td>
                          <td style={{ fontWeight: 600, color: '#0369a1' }}>
                            {v.startTime || '09:00'} - {v.endTime || '11:00'}
                          </td>
                          <td>
                            <strong
                              className="table-wp-link"
                              onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                              title="İşyeri Detayını Aç"
                            >
                              {v.workplace?.title || 'Firma'}
                            </strong>
                          </td>
                          <td>{renderDangerBadge(v.workplace?.dangerClass)}</td>
                          <td style={{ fontSize: '12px', color: '#334155' }}>{v.visitType}</td>
                          <td style={{ fontSize: '11.5px', color: v.notes ? '#334155' : '#94a3b8' }}>
                            {v.notes || '-'}
                          </td>
                          <td className="table-status-cell">
                            <span className={`table-status-pill ${isPlanned ? 'planned' : 'completed'}`}>
                              {isPlanned ? '📅 Planlandı' : '✅ Tamamlandı'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div className="table-actions-flex">
                              {isPlanned && (() => {
                                const check = canMarkVisitAsCompleted(v.visitDate, v.startTime);
                                const isEligible = check.allowed;
                                return (
                                  <button
                                    type="button"
                                    className={`btn-table-act green ${!isEligible ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => handleCompleteVisit(v.id)}
                                    title={isEligible ? 'Ziyareti Tamamla' : (check.reason || 'Ziyaret günü ve saati gelmeden tamamlanamaz')}
                                    style={!isEligible ? { opacity: 0.5, cursor: 'not-allowed', filter: 'grayscale(0.6)' } : undefined}
                                  >
                                    ✓
                                  </button>
                                );
                              })()}
                              <button
                                type="button"
                                className="btn-table-act blue"
                                onClick={() => handleOpenWorkplaceDetail(v.workplaceId)}
                                title="İşyeri Kartını Aç"
                              >
                                🏢
                              </button>
                              <button
                                type="button"
                                className="btn-table-act red"
                                onClick={() => handleDeleteVisit(v.id)}
                                title="Ziyareti Sil"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      )}

      {/* Ziyaret Planlama Modalı (Çakışma Kontrollü) */}
      <PlanVisitModal
        isOpen={isPlanModalOpen}
        onClose={() => setIsPlanModalOpen(false)}
        workplace={selectedWpForModal}
        workplacesList={workplaces}
        initialDate={modalInitialDate}
        allowChangeWorkplace={true}
        onVisitCreated={(newVisit) => {
          loadData();
        }}
      />

      {/* İşyeri Detay Modalı */}
      <WorkplaceDetailModal
        workplace={detailWorkplace}
        isOpen={isDetailModalOpen}
        onClose={() => {
          setIsDetailModalOpen(false);
          loadData();
        }}
        onEdit={() => {}}
        onDelete={() => {}}
        onUpdate={() => loadData()}
      />
    </div>
  );
};

export default PlanningPage;
